import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Play, Pause, Mic } from "lucide-react";

const fmt = (s) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

// Plays an access-controlled voice note via the API (sends auth cookie) as a blob.
export default function VoiceNote({ path, seconds, mine }) {
  const [url, setUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(seconds || 0);
  const audio = useRef();
  useEffect(() => {
    let u;
    api.get(`/island/audio/${path}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch(() => {});
    return () => u && URL.revokeObjectURL(u);
  }, [path]);
  const toggle = () => { const a = audio.current; if (!a) return; if (playing) { a.pause(); } else { a.play(); } };
  const pct = dur ? Math.min(100, (t / dur) * 100) : 0;
  return (
    <div className="flex min-w-[200px] items-center gap-2" data-testid="voice-note">
      <button onClick={toggle} disabled={!url} className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${mine ? "bg-white/20" : "bg-[hsl(var(--gold))]/20 text-[hsl(var(--gold))]"}`} data-testid="voice-play-btn">
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      <div className="flex-1">
        <div className={`h-1.5 w-full overflow-hidden rounded-full ${mine ? "bg-white/30" : "bg-secondary"}`}><div className={`h-full ${mine ? "bg-white" : "bg-[hsl(var(--gold))]"}`} style={{ width: `${pct}%` }} /></div>
        <p className={`mt-1 flex items-center gap-1 text-[0.65rem] ${mine ? "text-white/80" : "text-muted-foreground"}`}><Mic className="h-3 w-3" />{fmt(playing ? t : dur)}</p>
      </div>
      {url && <audio ref={audio} src={url} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); setT(0); }} onTimeUpdate={(e) => setT(e.target.currentTime)} onLoadedMetadata={(e) => isFinite(e.target.duration) && setDur(e.target.duration)} />}
    </div>
  );
}

export function useVoiceRecorder() {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const rec = useRef(); const chunks = useRef([]); const timer = useRef(); const stream = useRef();
  const start = async () => {
    stream.current = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus") ? "audio/webm;codecs=opus" : (MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "");
    rec.current = new MediaRecorder(stream.current, mime ? { mimeType: mime } : undefined);
    chunks.current = [];
    rec.current.ondataavailable = (e) => e.data.size && chunks.current.push(e.data);
    rec.current.start(250);
    setElapsed(0); setRecording(true);
    timer.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };
  const stop = () => new Promise((resolve) => {
    const r = rec.current; if (!r) return resolve(null);
    r.onstop = () => {
      clearInterval(timer.current); setRecording(false);
      stream.current?.getTracks().forEach((tr) => tr.stop());
      const type = r.mimeType || "audio/webm";
      const blob = new Blob(chunks.current, { type });
      resolve(blob.size ? { blob, ext: type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm" } : null);
    };
    r.stop();
  });
  const cancel = () => { const r = rec.current; if (r && r.state !== "inactive") { r.onstop = null; r.stop(); } clearInterval(timer.current); stream.current?.getTracks().forEach((tr) => tr.stop()); setRecording(false); };
  return { recording, elapsed, start, stop, cancel };
}
